import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-badge-reusable',
  standalone: true,
  imports: [CommonModule],
  template: `
    <span [ngClass]="computedClasses">
      <span
        *ngIf="dot"
        class="w-1.5 h-1.5 rounded-full"
        [ngClass]="dotColor"
      ></span>
      <span>{{ text }}</span>
      <button
        *ngIf="removable"
        (click)="onRemove()"
        class="text-xs opacity-70 hover:opacity-100 ml-1"
      >
        ×
      </button>

    </span>
  `,
})
export class BadgeComponent {
  @Input() text: string = 'Badge';

  @Input() variant:
    | 'neutral'
    | 'red'
    | 'amber'
    | 'green'
    | 'blue'
    | 'indigo'
    | 'purple'
    | 'orange' = 'neutral';

  @Input() size: 'sm' | 'md' = 'md';
  @Input() bordered: boolean = true;
  @Input() flat: boolean = false;
  @Input() dot: boolean = false;
  @Input() removable: boolean = false;

  @Output() remove = new EventEmitter<void>();

  onRemove() {
    this.remove.emit();
  }

  private base =
    'inline-flex items-center justify-center box-border rounded-md BH6 leading-4';

  private sizeMap = {
    sm: 'px-[10px] py-[5px] gap-[5px] text-[12px]',
    md: ' px-[10px] py-[5px] gap-[5px] text-[12px]',
  };

  private readonly variantMap: Record<
    NonNullable<BadgeComponent['variant']>,
    { bg: string; border: string; text: string; dot: string }
  > = {
    neutral: {
      bg: 'bg-slate50',
      border: 'border-slate200',
      text: 'text-slate800',
      dot: 'bg-slate600',
    },
    red: {
      bg: 'bg-red50',
      border: 'border-red200',
      text: 'text-red800',
      dot: 'bg-red600',
    },
    amber: {
      bg: 'bg-amber50',
      border: 'border-amber200',
      text: 'text-amber800',
      dot: 'bg-amber600',
    },
    green: {
      bg: 'bg-green50',
      border: 'border-green200',
      text: 'text-green800',
      dot: 'bg-green600',
    },
    blue: {
      bg: 'bg-blue50',
      border: 'border-blue200',
      text: 'text-blue800',
      dot: 'bg-blue600',
    },
    indigo: {
      bg: 'bg-indigo50',
      border: 'border-indigo200',
      text: 'text-indigo800',
      dot: 'bg-indigo600',
    },
    purple: {
      bg: 'bg-purple50',
      border: 'border-purple200',
      text: 'text-purple800',
      dot: 'bg-purple600',
    },
    orange: {
      bg: 'bg-orange50',
      border: 'border-orange200',
      text: 'text-orange800',
      dot: 'bg-orange600',
    },
  };

  private readonly flatDotMap: Record<NonNullable<BadgeComponent['variant']>, string> = {
    neutral: 'bg-slate400',
    red: 'bg-red400',
    amber: 'bg-amber400',
    green: 'bg-green400',
    blue: 'bg-blue400',
    indigo: 'bg-indigo400',
    purple: 'bg-purple400',
    orange: 'bg-orange400',
  };

  get computedClasses(): string {
    const v = this.variantMap[this.variant];
    let bgClass = '';
    let borderClass = '';
    let textClass = v.text;

    if (this.flat) {
      bgClass = v.bg;
      borderClass = 'border border-transparent';
    }
    else if (this.bordered && this.dot && !this.removable) {
      bgClass = 'bg-white';
      borderClass = 'border border-slate200';
      textClass = 'text-slate800';
    }
    else if (this.bordered && this.removable) {
      bgClass = v.bg;
      borderClass = `border ${v.border}`;
    }
    else if (this.bordered) {
      bgClass = v.bg;
      borderClass = `border ${v.border}`;
    }
    else {
      bgClass = v.bg;
      borderClass = 'border border-transparent';
    }

    return [
      this.base,
      this.sizeMap[this.size],
      bgClass,
      borderClass,
      textClass,
    ].join(' ');
  }
  get dotColor(): string {
    return this.flat ? this.flatDotMap[this.variant] : this.variantMap[this.variant].dot;
  }
}